import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Briefcase, Loader2, CheckCircle, ShieldCheck, HandshakeIcon, ArrowRight, Pencil, Send, Clock, Ban
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import InnovatorSubmissionPanel from '@/components/InnovatorSubmissionPanel';

function renderMarkdown(md) {
  if (!md) return '';
  let html = md.replace(/</g, '&lt;')
    .replace(/^# (.+)$/gm, '<h1 class="text-lg font-bold mt-3 mb-2">$1</h1>')
    .replace(/^## (.+)$/gm, '<h2 class="text-base font-semibold mt-3 mb-1">$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  return html.split('\n').map((l) =>
    /^\s*[-*] /.test(l) ? `<li>${l.replace(/^\s*[-*] /, '')}</li>` :
    l.trim() === '' ? '<br/>' :
    !l.startsWith('<h') ? `<p class="text-sm">${l}</p>` : l
  ).join('\n');
}

export default function ExpertAvailableProjects() {
  const { token, API } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Proposal editing state
  const [editingProposal, setEditingProposal] = useState(false);
  const [proposalDraft, setProposalDraft] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [numInstallments, setNumInstallments] = useState(3);
  const [savingProposal, setSavingProposal] = useState(false);
  const [moduleDisabled, setModuleDisabled] = useState('');

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/projects/expert/available`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProjects(data.projects || []);
        setModuleDisabled(data.module_disabled ? data.message : '');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) fetchProjects(); /* eslint-disable-line */ }, [token]);

  // When opening a selected project, seed proposal editor with existing content
  useEffect(() => {
    if (selected && selected.is_selected) {
      setProposalDraft(selected.final_proposal_markdown || selected.proposal_markdown || '');
      setTotalAmount(selected.total_amount_inr ? String(selected.total_amount_inr) : '');
      setNumInstallments(selected.num_installments || 3);
      setEditingProposal(false);
    }
  }, [selected]);

  const submitInterest = async () => {
    if (!accepted) { toast.error('Please accept the Terms & Conditions'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/projects/${selected.id}/express-interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ notes, accepts_terms: true })
      });
      if (res.ok) {
        toast.success('Interest expressed! Admin will review.');
        setSelected(null); setNotes(''); setAccepted(false);
        fetchProjects();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitProposal = async () => {
    if ((proposalDraft || '').trim().length < 100) {
      toast.error('Proposal is too short. Please provide a complete brief.'); return;
    }
    const amt = Number(totalAmount);
    if (!amt || amt < 1) { toast.error('Enter a valid total project amount'); return; }
    if (numInstallments < 1 || numInstallments > 6) { toast.error('Installments must be between 1 and 6'); return; }
    setSavingProposal(true);
    try {
      const res = await fetch(`${API}/projects/${selected.id}/innovator-submit-proposal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          proposal_markdown: proposalDraft,
          total_amount_inr: amt,
          num_installments: Number(numInstallments),
        })
      });
      if (res.ok) {
        toast.success('Proposal submitted. The industry will review shortly.');
        setEditingProposal(false);
        setSelected(null);
        fetchProjects();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed');
      }
    } finally {
      setSavingProposal(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  if (moduleDisabled) {
    return (
      <div className="text-center py-12 px-4 rounded-lg border border-amber-500/40 bg-amber-500/5" data-testid="expert-projects-disabled">
        <Ban className="w-10 h-10 mx-auto mb-3 text-amber-600" />
        <p className="font-medium text-amber-800">Projects are disabled</p>
        <p className="text-sm text-amber-700 mt-1 max-w-md mx-auto">{moduleDisabled} Please contact your university administrator.</p>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p>No matching projects right now.</p>
        <p className="text-xs mt-1">We&apos;ll email you when a new project matches your expertise.</p>
      </div>
    );
  }

  const proposalAccepted = selected?.proposal_accepted_at;

  return (
    <>
      <div className="space-y-3">
        {projects.map((p) => (
          <Card key={p.id} className="animate-fade-in" data-testid={`available-project-${p.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <h4 className="font-semibold">{p.title}</h4>
                {p.is_selected && p.proposal_accepted_at ? (
                  <Badge className="bg-emerald-500/10 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Proposal Accepted</Badge>
                ) : p.is_selected && p.proposal_submitted_at ? (
                  <Badge className="bg-amber-500/10 text-amber-700"><Clock className="w-3 h-3 mr-1" />Proposal Under Review</Badge>
                ) : p.is_selected ? (
                  <Badge className="bg-green-500/10 text-green-600"><CheckCircle className="w-3 h-3 mr-1" />Selected — Submit Proposal</Badge>
                ) : p.my_interest?.status === 'selected' ? (
                  <Badge className="bg-green-500/10 text-green-600">Selected</Badge>
                ) : p.my_interest?.status === 'rejected' ? (
                  <Badge className="bg-zinc-500/10 text-zinc-600">Not selected</Badge>
                ) : p.my_interest ? (
                  <Badge className="bg-amber-500/10 text-amber-600">Interest Submitted</Badge>
                ) : (
                  <Badge variant="secondary">Open</Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground line-clamp-3 mb-3">{p.prompt}</p>
              <Button size="sm" variant="outline" onClick={() => setSelected(p)} className="gap-2" data-testid={`open-project-${p.id}`}>
                {p.is_selected && !p.proposal_submitted_at ? 'Review & Submit Proposal' : 'View Project'} <ArrowRight className="w-3 h-3" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) { setSelected(null); setNotes(''); setAccepted(false); setEditingProposal(false); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>{selected.title}</DialogTitle>
                <DialogDescription>
                  {selected.is_selected
                    ? "You've been selected. Review the Requirement Brief, edit it into your finalized Proposal, then submit."
                    : "Project brief from an industry partner. Express interest to be considered."}
                </DialogDescription>
              </DialogHeader>

              {/* Show the Requirement Brief */}
              {!selected.is_selected && (
                <div className="prose prose-sm max-w-none border rounded p-4 bg-muted/30" dangerouslySetInnerHTML={{ __html: renderMarkdown(selected.proposal_markdown) }} />
              )}

              {/* Express interest for non-selected experts */}
              {!selected.my_interest && !selected.is_selected && (
                <div className="space-y-3 mt-4">
                  <label className="text-sm font-medium">Why are you a good fit? (optional)</label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Briefly describe why this project aligns with your expertise…" />
                  <label className="flex items-start gap-2 text-sm">
                    <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} className="mt-1" />
                    <span className="text-muted-foreground">
                      I accept CollabHive&apos;s <b>Terms &amp; Conditions</b>, including the IP terms in the brief,
                      the 20% platform success fee retained by CollabHive, and the payout schedule.
                    </span>
                  </label>
                  <DialogFooter>
                    <Button onClick={submitInterest} disabled={submitting || !accepted} className="gap-2" data-testid="express-interest-submit">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <HandshakeIcon className="w-4 h-4" />}
                      Express Interest
                    </Button>
                  </DialogFooter>
                </div>
              )}

              {/* Proposal editor for selected innovators */}
              {selected.is_selected && (
                <div className="mt-4 space-y-3">
                  {!selected.proposal_submitted_at && !editingProposal && (
                    <div className="rounded-md border p-4 bg-primary/5">
                      <div className="flex items-center gap-2 mb-2">
                        <Pencil className="w-4 h-4 text-primary" />
                        <h3 className="font-semibold text-sm">Prepare your Finalized Proposal</h3>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">
                        Edit the AI-drafted Requirement Brief into your finalized Proposal. Adjust scope, timeline,
                        deliverables, and add your total project value and preferred installment count. The industry
                        will review and accept before payments begin.
                      </p>
                      <Button size="sm" onClick={() => setEditingProposal(true)} className="gap-2" data-testid="start-edit-proposal">
                        <Pencil className="w-4 h-4" /> Edit &amp; Submit Proposal
                      </Button>
                    </div>
                  )}

                  {(!selected.proposal_submitted_at && editingProposal) && (
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Proposal (Markdown supported)</label>
                        <Textarea
                          value={proposalDraft}
                          onChange={(e) => setProposalDraft(e.target.value)}
                          rows={16}
                          className="font-mono text-xs mt-1"
                          data-testid="proposal-markdown-input"
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">{proposalDraft.length} chars · {(proposalDraft.match(/\n/g) || []).length + 1} lines</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">Total Project Value (INR)</label>
                          <Input type="number" min="1" value={totalAmount} onChange={(e) => setTotalAmount(e.target.value)} placeholder="e.g. 3000000" data-testid="total-amount-input" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">Number of Installments</label>
                          <Input type="number" min="1" max="6" value={numInstallments} onChange={(e) => setNumInstallments(e.target.value)} data-testid="num-installments-input" />
                          <p className="text-[10px] text-muted-foreground mt-1">3 = 40/30/30 · other = even split</p>
                        </div>
                      </div>
                      <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setEditingProposal(false)} disabled={savingProposal}>Cancel</Button>
                        <Button onClick={submitProposal} disabled={savingProposal} className="gap-2" data-testid="submit-proposal-btn">
                          {savingProposal ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          Submit Proposal to Industry
                        </Button>
                      </DialogFooter>
                    </div>
                  )}

                  {/* View submitted proposal */}
                  {selected.proposal_submitted_at && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-emerald-500/10 text-emerald-700">Your Finalized Proposal</Badge>
                        {selected.total_amount_inr > 0 && (
                          <span className="text-xs text-muted-foreground">
                            ₹{selected.total_amount_inr.toLocaleString('en-IN')} · {selected.num_installments} installments
                          </span>
                        )}
                      </div>
                      <div className="prose prose-sm max-w-none border rounded p-4 bg-muted/30 max-h-96 overflow-y-auto" dangerouslySetInnerHTML={{ __html: renderMarkdown(selected.final_proposal_markdown) }} />
                      {!proposalAccepted && (
                        <p className="mt-3 text-sm text-amber-700 flex items-center gap-1">
                          <Clock className="w-4 h-4" /> Awaiting industry review &amp; acceptance
                        </p>
                      )}
                      {proposalAccepted && (
                        <p className="mt-3 text-sm text-emerald-700 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" /> Proposal accepted — payment being released per milestones
                        </p>
                      )}
                    </div>
                  )}

                  {/* Execution guide — visible ONLY after industry accepts the finalized Proposal */}
                  {proposalAccepted && selected.step_guide_markdown && (
                    <div className="mt-4">
                      <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-green-600" /> Execution Guide
                      </h3>
                      <p className="text-xs text-muted-foreground mb-2">
                        The industry has accepted your proposal. Follow this execution guide for milestone delivery.
                      </p>
                      <div className="prose prose-sm max-w-none border rounded p-4 bg-green-500/5 max-h-96 overflow-y-auto" dangerouslySetInnerHTML={{ __html: renderMarkdown(selected.step_guide_markdown) }} />
                    </div>
                  )}

                  {/* Milestone Submissions — visible after industry accepts proposal */}
                  {proposalAccepted && (
                    <InnovatorSubmissionPanel
                      project={selected}
                      API={API}
                      token={token}
                      onDone={() => { fetchProjects(); }}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
